import { api } from "@/lib/api";

/** Brand Site API 错误（c456-brand-site 返回 `{ error: string }`） */
export class BrandSiteApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly payload: unknown,
  ) {
    super(extractBrandSiteErrorMessage(payload) ?? `请求失败（${status}）`);
    this.name = "BrandSiteApiError";
  }
}

export type BrandSiteStatus = "empty" | "generating" | "draft_ready" | "published" | "failed";

export type BrandSiteRelease = {
  id: number;
  number: number;
  published_at: string;
  agent_score?: number | null;
  agent_grade?: string | null;
  entity_snapshot?: Record<string, unknown>;
};

/** 正式版列表项（含是否当前正式版） */
export type BrandSiteReleaseListItem = BrandSiteRelease & {
  current: boolean;
};

/** 正式版详情中的页面（html 经 blob） */
export type BrandSiteReleasePage = {
  slug: string;
  title: string;
  is_home: boolean;
  html: string;
};

export type BrandSitePurpose = "customer" | "platform_template";

export type BrandSite = {
  id: number;
  account_id: number;
  brand_id: number;
  slug: string;
  title: string;
  purpose: BrandSitePurpose | string;
  is_primary: boolean;
  status: BrandSiteStatus | string;
  public_url?: string;
  custom_host?: string | null;
  created_at?: string;
  updated_at?: string;
  current_release?: BrandSiteRelease | null;
  agent_score?: number | null;
  agent_grade?: string | null;
  agent_checked_at?: string | null;
  agent_check_details?: {
    checks?: Record<string, "pass" | "fail" | "warn" | "skip">;
  } | null;
  pages_count?: number | null;
  authoring_brief?: {
    template_slug?: string;
    template_title?: string;
    style_notes?: string;
    do_not_change?: string[];
    plan_summary?: string;
    updated_at?: string;
  };
};

export type BrandSitePage = {
  id: number;
  slug: string;
  title: string;
  html_content: string;
  is_home: boolean;
};

export type BrandSiteCollection = {
  id: number;
  key: string;
  nav_label: string;
  list_template_html: string;
  item_template_html: string;
  /** 条目模板 {% schema %} 解析结果（服务端下发，表单免再解析 Liquid） */
  item_schema?: BrandSiteItemSchema;
};

export type BrandSiteItemSchemaField = {
  id: string;
  type: string;
  label?: string;
  default?: string;
  /** 兼容：部分模板误用 name/key 当字段键（归一化后应写入 id） */
  name?: string;
  key?: string;
};

export type BrandSiteItemSchema = {
  name?: string;
  settings?: BrandSiteItemSchemaField[];
};

export type BrandSiteCollectionItem = {
  id: number;
  slug: string;
  settings: Record<string, unknown>;
  url: string;
};

export type BrandSitePreview = {
  path: string;
  kind: string;
  html: string;
  title: string;
};

export const BRAND_SITE_STATUS_LABELS: Record<BrandSiteStatus, string> = {
  empty: "尚未生成",
  generating: "正在生成",
  draft_ready: "草稿待发布",
  published: "已上线",
  failed: "生成失败",
};

export function brandSiteStatusLabel(status: string): string {
  return BRAND_SITE_STATUS_LABELS[status as BrandSiteStatus] ?? status;
}

const STATUS_BADGE_BASE = "rounded-full px-2 py-0.5 text-[11px]";

/** 列表「访问」：只有已上线才能打开前台 */
export function brandSiteCanVisit(site: Pick<BrandSite, "status">): boolean {
  return site.status === "published";
}

/** 列表状态胶囊：未发布用琥珀色强调，已上线用绿色，失败用警示色 */
export function brandSiteStatusBadgeClass(status: string): string {
  switch (status) {
    case "published":
      return `${STATUS_BADGE_BASE} bg-emerald-500/10 text-emerald-700 dark:text-emerald-400`;
    case "empty":
    case "draft_ready":
      return `${STATUS_BADGE_BASE} bg-amber-500/15 text-amber-800 dark:text-amber-300`;
    case "generating":
      return `${STATUS_BADGE_BASE} bg-sky-500/10 text-sky-700 dark:text-sky-400`;
    case "failed":
      return `${STATUS_BADGE_BASE} bg-destructive/10 text-destructive`;
    default:
      return `${STATUS_BADGE_BASE} bg-muted text-muted-foreground`;
  }
}

/** 记分详情含 platform JSON-LD 时给商户的短提示；不挡发布 */
export function brandSitePlatformJsonLdCopy(
  site: Pick<BrandSite, "agent_check_details">,
): string | null {
  const status = site.agent_check_details?.checks?.["platform-jsonld-script"];
  if (status !== "pass") return null;
  return "含结构化数据（发布时由平台自动注入，不用手写）。";
}

/** 发布后的智能体可读性：给商家看分数，不挡住发布 */
export function brandSiteAgentScoreCopy(
  site: Pick<BrandSite, "agent_score" | "agent_grade"> & {
    agent_check_details?: BrandSite["agent_check_details"];
  },
): {
  title: string;
  hint?: string;
  jsonLdHint?: string;
} | null {
  if (site.agent_score == null || !site.agent_grade) return null;
  const title = `智能体可读性 ${site.agent_grade}（${site.agent_score}）`;
  const jsonLdHint = brandSitePlatformJsonLdCopy(site) ?? undefined;
  if (site.agent_score >= 90) {
    return {
      title,
      hint: "AI 助手比较容易读懂这个网站。分数只作参考，不挡住发布。",
      jsonLdHint,
    };
  }
  return {
    title,
    hint: "分数只作参考，不挡住发布。页面太少或太大时会偏低；补全关于、服务、联系、常见问题后再发一次。",
    jsonLdHint,
  };
}

export type BrandSiteAgentScorePreview = {
  score: number;
  grade: string;
  pages_count: number;
  capped: boolean;
  details?: Record<string, unknown>;
};

export function previewBrandSiteAgentScore(accountId: string | number, brandSiteId: string | number) {
  return brandSiteRequest<BrandSiteAgentScorePreview>(
    accountId,
    `/brand_sites/${brandSiteId}/agent_score_preview`,
    { method: "POST" },
  );
}

export function brandSiteDraftScoreCopy(
  preview: Pick<BrandSiteAgentScorePreview, "score" | "grade" | "capped"> | null,
): { title: string; hint: string } | null {
  if (!preview?.grade || preview.score == null) return null;
  return {
    title: `草稿预评 ${preview.grade}（${preview.score}）`,
    hint: preview.capped
      ? "页面偏少时分数会封顶；补全关于、服务、联系、常见问题等后再评。分数只参考，不挡住发布。"
      : "按当前草稿估算，发布后才会写入正式分。分数只参考，不挡住发布。",
  };
}

/** 正式版列表用的短评分文案，如 `A（95）`；无评分时返回 null */
export function brandSiteReleaseScoreLabel(
  release: Pick<BrandSiteRelease, "agent_score" | "agent_grade">,
): string | null {
  if (release.agent_score == null || !release.agent_grade) return null;
  return `${release.agent_grade}（${release.agent_score}）`;
}

/** 草稿/已发布页少于 5 时的商户提示；不挡住发布 */
export function brandSiteTooFewPagesCopy(
  site: Pick<BrandSite, "pages_count" | "status">,
): string | null {
  const count = site.pages_count;
  if (count == null || count <= 0 || count >= 5) return null;
  return "智能体可读性还不够（页太少）。建议补到至少 5 页后再发，现在也可以发布。";
}

/**
 * 给「打开网站」用的 URL：平时公开页仍可 max-age 缓存；
 * 带上当前正式版 id 作 cache-bust，发布后点开必看到新站。
 */
export function brandSiteVisitUrl(
  site: Pick<BrandSite, "public_url" | "slug" | "current_release" | "custom_host">,
  options?: { preferCustomHost?: boolean },
): string | null {
  const preferCustom = options?.preferCustomHost === true;
  const customHost = site.custom_host?.trim();
  let base: string | null = null;
  if (preferCustom && customHost) {
    base = customHost.includes("://") ? customHost : `https://${customHost}`;
  } else {
    base =
      site.public_url?.trim() ||
      (site.slug ? `https://${site.slug}.web.c456.com` : null) ||
      (customHost ? (customHost.includes("://") ? customHost : `https://${customHost}`) : null);
  }
  if (!base) return null;

  const releaseId = site.current_release?.id;
  if (releaseId == null) return base;

  try {
    const url = new URL(base);
    url.searchParams.set("v", String(releaseId));
    return url.toString();
  } catch {
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}v=${releaseId}`;
  }
}

/**
 * 打开某条公网 path（如 `/news/foo`）；保留正式版 cache-bust。
 * 公共部分（`/_…`）返回 null。
 */
export function brandSiteFrontendPageUrl(
  site: Pick<BrandSite, "public_url" | "slug" | "current_release" | "custom_host">,
  pagePath: string,
  options?: { preferCustomHost?: boolean },
): string | null {
  const raw = pagePath.trim() || "/";
  const pathname = raw.startsWith("/") ? raw : `/${raw}`;
  if (pathname.startsWith("/_")) return null;

  const home = brandSiteVisitUrl(site, options);
  if (!home) return null;

  try {
    const url = new URL(home);
    url.pathname = pathname === "/" ? "/" : pathname.replace(/\/+$/, "") || "/";
    return url.toString();
  } catch {
    if (pathname === "/") return home;
    const q = home.includes("?") ? home.slice(home.indexOf("?")) : "";
    const origin = home.replace(/\/?(?:\?.*)?$/, "");
    return `${origin}${pathname}${q}`;
  }
}

/**
 * 左侧预览 iframe 改写 /_c456/a/… 用的 origin：public_url 优先，否则 slug 拼平台域。
 * 不含发布版 cache-bust 查询参数（否则会拼坏素材路径）。
 */
export function brandSiteAssetBaseUrl(
  site: Pick<BrandSite, "public_url" | "slug" | "custom_host"> & {
    public_url?: string | null;
    slug?: string | null;
    custom_host?: string | null;
  },
): string | null {
  const raw =
    site.public_url?.trim() ||
    (site.slug?.trim() ? `https://${site.slug.trim()}.web.c456.com` : null) ||
    (site.custom_host?.trim()
      ? site.custom_host.includes("://")
        ? site.custom_host.trim()
        : `https://${site.custom_host.trim()}`
      : null);
  if (!raw) return null;
  try {
    const url = new URL(raw.includes("://") ? raw : `https://${raw}`);
    if (!url.host) return null;
    const portSuffix =
      url.port && url.port !== "443" && url.port !== "80" ? `:${url.port}` : "";
    return `${url.protocol}//${url.hostname}${portSuffix}`;
  } catch {
    return null;
  }
}

function brandSiteBaseUrl(): string {
  const raw = import.meta.env.VITE_BRAND_SITE_API_URL as string | undefined;
  const base = (raw ?? "http://127.0.0.1:3020").replace(/\/$/, "");
  return base;
}

function resolveBrandSiteUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${brandSiteBaseUrl()}${normalized.startsWith("/api/") ? normalized : `/api/v1${normalized}`}`;
}

function extractBrandSiteErrorMessage(payload: unknown): string | undefined {
  if (typeof payload !== "object" || payload === null || !("error" in payload)) return undefined;
  const error = (payload as { error: unknown }).error;
  if (typeof error === "string") return error;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return undefined;
}

async function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const contentType = response.headers.get("content-type") ?? "";
  const text = await response.text();
  if (!text) return undefined;
  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

/** 带账户 JWT 调 c456-brand-site；Bearer + X-Account-Id */
export async function brandSiteRequest<T>(
  accountId: string | number,
  path: string,
  options: RequestInit = {},
  retried = false,
): Promise<T> {
  const headers = new Headers(options.headers);
  const accessToken = api.getAccessToken();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  headers.set("X-Account-Id", String(accountId));
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await window.fetch(resolveBrandSiteUrl(path), { ...options, headers });
  const payload = await parseResponse(response);

  if (response.status === 401 && !retried && (await api.refreshAccessToken())) {
    return brandSiteRequest<T>(accountId, path, options, true);
  }

  if (response.status === 401) {
    api.clearTokens();
    if (window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
  }

  if (!response.ok) throw new BrandSiteApiError(response.status, payload);
  return payload as T;
}

/** 读取品牌下全部 customer 站；无站时 200 且 brand_site 为 null */
export function fetchBrandSites(accountId: string | number, brandId: string | number) {
  return brandSiteRequest<{ brand_sites: BrandSite[]; brand_site: BrandSite | null }>(
    accountId,
    `/brand_sites?brand_id=${encodeURIComponent(String(brandId))}`,
  );
}

/** @deprecated 请用 fetchBrandSites */
export function fetchBrandSite(accountId: string | number, brandId: string | number) {
  return fetchBrandSites(accountId, brandId);
}

/** POST ensure primary：仅 brand_id 时 find_or_create 主站 */
export function ensureBrandSite(accountId: string | number, brandId: string | number) {
  return brandSiteRequest<{ brand_site: BrandSite }>(accountId, "/brand_sites", {
    method: "POST",
    body: JSON.stringify({ brand_id: Number(brandId) || brandId }),
  });
}

/** GET 单站（须属于当前账户） */
export function getBrandSite(accountId: string | number, brandSiteId: string | number) {
  return brandSiteRequest<{ brand_site: BrandSite }>(accountId, `/brand_sites/${brandSiteId}`);
}

/** POST 新建 customer 站；title 必填；平台子域由服务端随机分配 */
export function createBrandSite(
  accountId: string | number,
  attrs: { brandId: string | number; title: string },
) {
  const title = attrs.title.trim();
  return brandSiteRequest<{ brand_site: BrandSite }>(accountId, "/brand_sites", {
    method: "POST",
    body: JSON.stringify({
      brand_id: Number(attrs.brandId) || attrs.brandId,
      title,
    }),
  });
}

export function listBrandSitePages(accountId: string | number, brandSiteId: string | number) {
  return brandSiteRequest<{ pages: BrandSitePage[] }>(
    accountId,
    `/brand_sites/${brandSiteId}/pages`,
  );
}

export function listBrandSiteCollections(accountId: string | number, brandSiteId: string | number) {
  return brandSiteRequest<{ collections: BrandSiteCollection[] }>(
    accountId,
    `/brand_sites/${brandSiteId}/collections`,
  );
}

/** POST upsert：同 key 则更新列表/单篇模板与导航名 */
export function upsertBrandSiteCollection(
  accountId: string | number,
  brandSiteId: string | number,
  attrs: {
    key: string;
    nav_label: string;
    list_template_html: string;
    item_template_html: string;
  },
) {
  return brandSiteRequest<{ collection: BrandSiteCollection }>(
    accountId,
    `/brand_sites/${brandSiteId}/collections`,
    {
      method: "POST",
      body: JSON.stringify(attrs),
    },
  );
}

export function listBrandSiteCollectionItems(
  accountId: string | number,
  brandSiteId: string | number,
  collectionId: string | number,
) {
  return brandSiteRequest<{ items: BrandSiteCollectionItem[] }>(
    accountId,
    `/brand_sites/${brandSiteId}/collections/${collectionId}/items`,
  );
}

/** POST upsert：同 slug 则更新；成功后服务端整站 publish */
export function upsertBrandSiteCollectionItem(
  accountId: string | number,
  brandSiteId: string | number,
  collectionId: string | number,
  attrs: { slug: string; settings: Record<string, unknown> },
) {
  return brandSiteRequest<{ item: BrandSiteCollectionItem }>(
    accountId,
    `/brand_sites/${brandSiteId}/collections/${collectionId}/items`,
    {
      method: "POST",
      body: JSON.stringify({ slug: attrs.slug, settings: attrs.settings }),
    },
  );
}

export function deleteBrandSiteCollectionItem(
  accountId: string | number,
  brandSiteId: string | number,
  collectionId: string | number,
  itemId: string | number,
) {
  return brandSiteRequest<void>(
    accountId,
    `/brand_sites/${brandSiteId}/collections/${collectionId}/items/${itemId}`,
    { method: "DELETE" },
  );
}

export function previewBrandSitePage(
  accountId: string | number,
  brandSiteId: string | number,
  path: string,
) {
  const query = new URLSearchParams({ path });
  return brandSiteRequest<BrandSitePreview>(
    accountId,
    `/brand_sites/${brandSiteId}/preview?${query.toString()}`,
  );
}

/** 从 app 同域 API 拉取 JSON-LD 用 entity_snapshot（不编造；空 Wiki → {}） */
export function fetchEntitySnapshot(
  accountId: string | number,
  brandId: string | number,
  options?: { publicSiteUrl?: string },
) {
  const qs = new URLSearchParams();
  if (options?.publicSiteUrl) {
    qs.set("public_site_url", options.publicSiteUrl);
  }
  const query = qs.toString();
  const path =
    `/accounts/${accountId}/brands/${brandId}/entity_snapshot` + (query ? `?${query}` : "");
  return api.fetchForAccount<{ entity_snapshot: Record<string, unknown> }>(accountId, path);
}

/**
 * 发布正式版：先从 app 取 entity_snapshot，再 POST 给 brand-site。
 * snapshot 为空或拉取失败时仍允许发布（降级为 {}）。
 */
export async function publishBrandSite(
  accountId: string | number,
  brandSiteId: string | number,
  options?: { brandId?: string | number; publicSiteUrl?: string },
) {
  let brandId = options?.brandId;
  let publicSiteUrl = options?.publicSiteUrl;

  if (brandId == null || publicSiteUrl == null) {
    const { brand_site } = await getBrandSite(accountId, brandSiteId);
    brandId ??= brand_site.brand_id;
    publicSiteUrl ??= brand_site.public_url;
  }

  let entity_snapshot: Record<string, unknown> = {};
  try {
    const payload = await fetchEntitySnapshot(accountId, brandId, { publicSiteUrl });
    if (payload.entity_snapshot && typeof payload.entity_snapshot === "object") {
      entity_snapshot = payload.entity_snapshot;
    }
  } catch {
    // 不挡发布：快照失败时以空对象继续
    entity_snapshot = {};
  }

  return brandSiteRequest<{ brand_site: BrandSite; release: BrandSiteRelease }>(
    accountId,
    `/brand_sites/${brandSiteId}/publish`,
    {
      method: "POST",
      body: JSON.stringify({ entity_snapshot }),
    },
  );
}

/** 正式版列表按版号新到旧排序（API 通常已排序，前端再兜底） */
export function sortBrandSiteReleasesNewestFirst<T extends { number: number }>(releases: T[]): T[] {
  return [...releases].sort((a, b) => b.number - a.number);
}

export function listBrandSiteReleases(accountId: string | number, brandSiteId: string | number) {
  return brandSiteRequest<{ releases: BrandSiteReleaseListItem[] }>(
    accountId,
    `/brand_sites/${brandSiteId}/releases`,
  ).then((payload) => ({
    releases: sortBrandSiteReleasesNewestFirst(payload.releases ?? []),
  }));
}

export function getBrandSiteRelease(
  accountId: string | number,
  brandSiteId: string | number,
  releaseId: string | number,
) {
  return brandSiteRequest<{ release: BrandSiteRelease; pages: BrandSiteReleasePage[] }>(
    accountId,
    `/brand_sites/${brandSiteId}/releases/${releaseId}`,
  );
}

/** 指针回滚到指定正式版（不复制 HTML） */
export function rollbackBrandSiteRelease(
  accountId: string | number,
  brandSiteId: string | number,
  releaseId: string | number,
) {
  return brandSiteRequest<{ brand_site: BrandSite; release: BrandSiteRelease }>(
    accountId,
    `/brand_sites/${brandSiteId}/releases/${releaseId}/rollback`,
    { method: "POST" },
  );
}

export type BrandSitePageVersion = {
  id: number;
  source: "agent" | "human" | "restore" | string;
  html_content: string;
  created_at: string;
};

export function updateBrandSitePage(
  accountId: string | number,
  brandSiteId: string | number,
  pageId: string | number,
  attrs: { html_content?: string; title?: string; slug?: string; is_home?: boolean; source?: string },
) {
  return brandSiteRequest<{ page: BrandSitePage }>(
    accountId,
    `/brand_sites/${brandSiteId}/pages/${pageId}`,
    { method: "PATCH", body: JSON.stringify(attrs) },
  );
}

export function listBrandSitePageVersions(
  accountId: string | number,
  brandSiteId: string | number,
  pageId: string | number,
) {
  return brandSiteRequest<{ versions: BrandSitePageVersion[] }>(
    accountId,
    `/brand_sites/${brandSiteId}/pages/${pageId}/versions`,
  );
}

export function restoreBrandSitePage(
  accountId: string | number,
  brandSiteId: string | number,
  pageId: string | number,
  versionId: string | number,
) {
  return brandSiteRequest<{ page: BrandSitePage }>(
    accountId,
    `/brand_sites/${brandSiteId}/pages/${pageId}/restore`,
    { method: "POST", body: JSON.stringify({ version_id: versionId }) },
  );
}

/** 品牌站已登记资源（本站上传或引用账号素材） */
export type BrandSiteAsset = {
  id: number;
  slug: string;
  proxy_path: string;
  kind: string;
  filename: string;
  content_type: string | null;
  byte_size: number;
  material_id: number | null;
  created_at: string;
  updated_at: string;
};

/** 代发路径：写入 HTML 的 src/href */
export function brandSiteAssetProxyPath(publicId: string): string {
  const id = publicId.trim();
  return id ? `/_c456/a/${id}` : "";
}

/** 选资源器登记引用时的 context（与 Hermes page:/slug 对齐） */
export function brandSiteAssetReferenceContext(pageSlug?: string | null): string {
  const slug = pageSlug?.trim();
  if (!slug) return "picker:manual";
  return slug === "index" ? "page:/" : `page:/${slug}`;
}

export function brandSiteAssetCopySuccessMessage(proxyPath: string): string {
  return `已复制 ${proxyPath}，可粘贴到 HTML 的 src 或 href`;
}

/** 将账号素材登记到本站，返回可写入 HTML 的代发路径 */
export function referenceBrandSiteMaterial(
  accountId: string | number,
  brandSiteId: string | number,
  attrs: {
    material_id: number;
    context: string;
    filename?: string;
    content_type?: string | null;
    kind?: string;
    byte_size?: number;
  },
) {
  return brandSiteRequest<{ asset: Pick<BrandSiteAsset, "slug" | "proxy_path"> }>(
    accountId,
    `/brand_sites/${brandSiteId}/assets/reference`,
    {
      method: "POST",
      body: JSON.stringify(attrs),
    },
  );
}
